import { Cancel, Modal, Save } from "components/UI";
import ContactForm from "../AddContact/ContactForm";
import { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { useGetContactDetail, useUpdateContact } from "queries/contact";
import { useTranslation } from "react-i18next";
import { useAntdMessage } from "hooks/useAntdMessage";
import { Form, Spin } from "antd";
import { isEmpty, omitBy } from "lodash-es";
import InhouseContactPicker from "./InhouseContactPicker";
import { getInhouseCompanyNames } from "utils/contactValidation";

// Modal untuk memperbaiki kontak existing yang tidak lolos validasi internal
// saat dipilih di Step 4 (Detail) tambah jadwal. Memakai ContactForm yang sama
// dengan form tambah kontak agar aturan validasinya identik.
const EditContactModal = ({
  show,
  id,
  hideHandler,
  onSaved,
  meetingMode = false,
  inhouseUsers,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { message } = useAntdMessage();
  const updateContact = useUpdateContact();
  const [loadingMutation, setLoadingMutation] = useState(false);

  const { data, isLoading } = useGetContactDetail(id, {
    enabled: !!id && show,
  });

  const inhouseCompanies = useMemo(
    () => getInhouseCompanyNames(inhouseUsers),
    [inhouseUsers]
  );

  // Isi form dari detail terbaru. Disimpan di ref agar bisa dipanggil dari
  // afterOpenChange (saat modal sudah benar-benar terbuka & form ter-mount
  // stabil) — penting saat kontak yang sama dibuka lagi: referensi `data` tidak
  // berubah (ter-cache) sehingga efek tak terpicu, tetapi afterOpenChange tetap
  // jalan tiap kali modal dibuka.
  const prefill = () => {
    if (!data) return;
    form.setFieldsValue({
      type_id: data.type_ids?.map(String),
      group_id: data.group_id,
      name: data.name,
      salutation_id: data.salutation_id,
      phone: data.phone,
      company: data.company,
      address: data.address,
      email: data.email,
      birthday: data.birthday ? dayjs(data.birthday) : undefined,
      country_id: data.country_id,
      city_id: data.city_id,
      province_id: data.province_id,
      photo: data.photo,
    });
  };
  const prefillRef = useRef(prefill);
  prefillRef.current = prefill;

  // Fallback: bila detail baru tiba secara async saat modal sudah terbuka.
  useEffect(() => {
    if (show && data) prefillRef.current();
  }, [show, data]);

  // Isi field yang ada nilainya saja, lalu validasi supaya agen langsung lihat
  // bila data inhouse belum sesuai aturan. Perusahaan inhouse yang huruf besar
  // semua tetap lolos (lihat inhouseCompanies di ContactForm).
  const applyInhouseData = (values) => {
    const fields = omitBy(values, isEmpty);
    form.setFieldsValue(fields);
    form.validateFields(Object.keys(fields)).catch(() => {});
    message.success(t("inhouse_contact.applied"));
  };

  const submitHandler = () => {
    form
      .validateFields()
      .then((values) => {
        const { type_id, birthday, ...rest } = values;
        // Samakan dengan jalur update yang terbukti (Contact/Content/Detail.jsx):
        // kirim type_ids (array), group_id null-able, dan birthday hanya bila ada.
        const payload = {
          ...rest,
          id,
          type_ids: type_id?.map(Number),
          group_id: rest.group_id || null,
          ...(birthday
            ? { birthday: dayjs(birthday).format("YYYY-MM-DD") }
            : {}),
        };
        setLoadingMutation(true);
        updateContact.mutate(payload, {
          onSuccess: (response) => {
            setLoadingMutation(false);
            if (response.data.success) {
              message.success(t("contact_edited"));
              if (typeof onSaved === "function") {
                // Gabungkan detail penuh + field hasil update agar record yang
                // dikembalikan lengkap (respons update bisa parsial).
                onSaved({ ...data, ...response.data.data });
              }
            }
          },
          onError: () => {
            setLoadingMutation(false);
            message.error(t("contact_edited_failed"));
            updateContact.reset();
          },
        });
      })
      .catch((info) => {
        if (info.errorFields && info.errorFields[0]) {
          form.scrollToField(info.errorFields[0].name);
        }
      });
  };

  return (
    <Modal
      wrapClassName="contact-modal"
      title={t("edit_contact")}
      open={show}
      onCancel={hideHandler}
      maskClosable={false}
      style={{ top: 50 }}
      afterOpenChange={(open) => {
        if (open) prefillRef.current();
        else form.resetFields();
      }}
      footer={[
        <Cancel key="cancel" onClick={hideHandler} />,
        <Save key="save" loading={loadingMutation} onClick={submitHandler} />,
      ]}
    >
      <Spin spinning={isLoading}>
        {!isEmpty(inhouseUsers) && (
          <InhouseContactPicker
            key={id}
            users={inhouseUsers}
            onApply={applyInhouseData}
          />
        )}
        <ContactForm
          form={form}
          isAdd={false}
          id={id}
          meetingMode={meetingMode}
          inhouseCompanies={inhouseCompanies}
        />
      </Spin>
    </Modal>
  );
};

export default EditContactModal;
